import React from "react";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";

import marca1 from "../../static/media/AboutUs/marca_1.webp";
import marca2 from "../../static/media/AboutUs/marca_2.webp";
import marca3 from "../../static/media/AboutUs/marca_3.webp";
import marca4 from "../../static/media/AboutUs/marca_4.webp";
import marca5 from "../../static/media/AboutUs/marca_5.webp";
import marca6 from "../../static/media/AboutUs/marca_6.webp";
import marca7 from "../../static/media/AboutUs/marca_7.webp";
import marca8 from "../../static/media/AboutUs/marca_8.webp";

const PRIMARY = "#06BBC7";
const TEXT = "#111827";

const MotionBox = motion(Box);

const BrandsPresentation = () => {
  const brands = [marca1, marca2, marca3, marca4, marca5, marca6, marca7, marca8];

  const dots = [
    { top: "12%", left: "8%", size: 7, duration: 6 },
    { top: "18%", left: "86%", size: 9, duration: 7 },
    { top: "38%", left: "15%", size: 5, duration: 5 },
    { top: "48%", left: "92%", size: 6, duration: 6.5 },
    { top: "72%", left: "7%", size: 8, duration: 8 },
    { top: "80%", left: "78%", size: 5, duration: 5.5 },
    { top: "28%", left: "52%", size: 6, duration: 7.5 },
    { top: "88%", left: "42%", size: 7, duration: 6 },
    { top: "58%", left: "30%", size: 4, duration: 5 },
    { top: "66%", left: "62%", size: 6, duration: 7 },
    { top: "10%", left: "35%", size: 5, duration: 6.8 },
    { top: "42%", left: "72%", size: 4, duration: 5.8 },
  ];

  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        maxWidth: "100vw",
        bgcolor: "#ffffff",
        px: { xs: 2, sm: 3, md: 5 },
        py: { xs: 6, sm: 8, md: 10 },
        overflow: "hidden",
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <MotionBox
          animate={{
            x: [0, 28, -18, 0],
            y: [0, -24, 18, 0],
          }}
          transition={{
            duration: 16,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          sx={{
            position: "absolute",
            top: -130,
            right: -120,
            width: { xs: 230, md: 360 },
            height: { xs: 230, md: 360 },
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(6,187,199,0.14) 0%, rgba(6,187,199,0.04) 48%, rgba(6,187,199,0) 72%)",
          }}
        />

        <MotionBox
          animate={{
            x: [0, -24, 20, 0],
            y: [0, 20, -22, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          sx={{
            position: "absolute",
            bottom: -140,
            left: -110,
            width: { xs: 240, md: 390 },
            height: { xs: 240, md: 390 },
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(247,201,72,0.16) 0%, rgba(247,201,72,0.05) 48%, rgba(247,201,72,0) 72%)",
          }}
        />

        {dots.map((dot, index) => (
          <MotionBox
            key={index}
            animate={{
              y: [0, -18, 10, 0],
              x: [0, 10, -8, 0],
              opacity: [0.25, 0.75, 0.35, 0.25],
              scale: [1, 1.35, 0.9, 1],
            }}
            transition={{
              duration: dot.duration,
              repeat: Infinity,
              ease: "easeInOut",
              delay: index * 0.2,
            }}
            sx={{
              position: "absolute",
              top: dot.top,
              left: dot.left,
              width: dot.size,
              height: dot.size,
              borderRadius: "50%",
              bgcolor: PRIMARY,
              opacity: 0.35,
            }}
          />
        ))}

        <MotionBox
          animate={{
            backgroundPosition: ["0px 0px", "80px 80px"],
          }}
          transition={{
            duration: 22,
            repeat: Infinity,
            ease: "linear",
          }}
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.32,
            backgroundImage: `radial-gradient(rgba(6,187,199,0.22) 1px, transparent 1px)`,
            backgroundSize: "34px 34px",
          }}
        />
      </Box>

      <Box
        sx={{
          width: "100%",
          maxWidth: 1180,
          mx: "auto",
          textAlign: "center",
          boxSizing: "border-box",
          position: "relative",
          zIndex: 1,
        }}
      >
        <MotionBox
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.25 }}
        >
          <Typography
            component="p"
            sx={{
              m: 0,
              mb: 1.4,
              color: PRIMARY,
              fontSize: { xs: "0.86rem", sm: "0.95rem" },
              fontWeight: 700,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
            }}
          >
            Marcas profesionales
          </Typography>

          <Typography
            component="h2"
            sx={{
              m: 0,
              mb: 1.8,
              color: TEXT,
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              lineHeight: 1.04,
              fontWeight: 800,
              letterSpacing: "-0.04em",
              overflowWrap: "break-word",
            }}
          >
            Trabajamos con las mejores marcas
          </Typography>

          <Typography
            sx={{
              maxWidth: 680,
              mx: "auto",
              color: "#4b5563",
              fontSize: { xs: "0.95rem", sm: "1.04rem" },
              lineHeight: { xs: 1.68, sm: 1.82 },
              fontWeight: 400,
              mb: { xs: 4, md: 5 },
            }}
          >
            Seleccionamos productos y líneas profesionales que acompañan cada
            tratamiento con calidad, confianza y resultados visibles.
          </Typography>
        </MotionBox>

        <Box
          sx={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(4, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: { xs: 2, sm: 2.4, md: 3 },
            alignItems: "center",
            boxSizing: "border-box",
          }}
        >
          {brands.map((brand, index) => (
            <MotionBox
              key={index}
              initial={{ opacity: 0, y: 24, scale: 0.94 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              whileHover={{ y: -7, scale: 1.035 }}
              transition={{
                duration: 0.42,
                ease: "easeOut",
                delay: index * 0.04,
              }}
              viewport={{ once: true, amount: 0.25 }}
              sx={{
                width: "100%",
                minHeight: { xs: 132, sm: 150, md: 168 },
                bgcolor: "rgba(255,255,255,0.82)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(6, 187, 199, 0.16)",
                borderRadius: { xs: "22px", sm: "28px" },
                display: "grid",
                placeItems: "center",
                p: { xs: 2, sm: 2.5, md: 3 },
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              <MotionBox
                component="img"
                src={brand}
                alt={`Marca profesional ${index + 1}`}
                loading="lazy"
                whileHover={{ scale: 1.06 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                sx={{
                  width: "100%",
                  maxWidth: { xs: 130, sm: 155, md: 178 },
                  height: { xs: 80, sm: 90, md: 104 },
                  objectFit: "contain",
                  objectPosition: "center",
                  display: "block",
                  filter: "saturate(1.02)",
                }}
              />
            </MotionBox>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default BrandsPresentation;